import React from 'react';
import { auth } from '../../../../config/firebase';
import { NavLink, useNavigate } from 'react-router-dom';
import {
	alpha,
	Box,
	Button,
	ClickAwayListener,
	Grow,
	MenuItem,
	MenuList,
	Paper,
	Popper,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { UserContext } from '../../../../provider/user';
import { NotificationContext } from '../../../../reducer/notification';
import { UserMenu } from '../types';
import AppAvatar from '../../../../components/content/AppAvatar';

interface Props {
	items: UserMenu;
	buttonText?: string;
}

export default function HeaderUserMenu({ items, buttonText }: Props) {
	const navigate = useNavigate();

	const [open, setOpen] = React.useState(false);
	const anchorRef = React.useRef<HTMLButtonElement>(null);

	const { dispatch: dispatchNotification } = React.useContext(NotificationContext);

	const { user } = React.useContext(UserContext);

	const handleToggle = () => {
		setOpen((prevOpen) => !prevOpen);
	};

	const handleClose = (event: MouseEvent | TouchEvent) => {
		if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
			return;
		}

		setOpen(false);
	};

	function handleListKeyDown(event: React.KeyboardEvent) {
		if (event.key === 'Tab') {
			event.preventDefault();
			setOpen(false);
		}
	}

	// return focus to the button when we transitioned from !open -> open
	const prevOpen = React.useRef(open);
	React.useEffect(() => {
		if (prevOpen.current === true && open === false) {
			anchorRef.current!.focus();
		}

		prevOpen.current = open;
	}, [open]);

	const handleLogout = async () => {
		dispatchNotification({
			type: 'notification/CLEAR',
		});

		try {
			await auth.signOut();
			navigate('/');
		} catch (error: any) {
			console.error(error);
			dispatchNotification({
				type: 'notification/SHOW_ERROR',
				payload: {
					message: error?.message ?? 'Unknown error',
				},
			});
		}
	};

	return (
		<>
			<Box sx={{ display: 'inline-block' }}>
				<Button
					ref={anchorRef}
					aria-controls={open ? 'user-menu' : undefined}
					aria-haspopup="true"
					onClick={handleToggle}
					endIcon={<ExpandMoreIcon color={'action'} />}
					sx={{
						color: (theme) =>
							alpha('light' === theme.palette.mode ? '#000' : '#fff', 0.87),
					}}
				>
					{buttonText ?? <AppAvatar user={user} style={{ width: 35, height: 35 }} />}
				</Button>
				<Popper
					id="user-menu"
					open={open}
					anchorEl={anchorRef.current}
					role={undefined}
					transition
					disablePortal
					placement="bottom-end"
				>
					{({ TransitionProps }) => (
						<Grow
							{...TransitionProps}
							style={{
								transformOrigin: 'right top',
							}}
						>
							<Paper>
								<ClickAwayListener onClickAway={handleClose}>
									<MenuList
										autoFocusItem={open}
										id="menu-list-grow"
										onKeyDown={handleListKeyDown}
									>
										{items.map(({ path, label }) => {
											return '/logout' === path ? (
												<MenuItem key={path} onClick={handleLogout}>
													{label}
												</MenuItem>
											) : (
												<MenuItem
													key={path}
													sx={{
														'&.active': {
															fontWeight: 700,
															color: 'secondary.main',
														},
													}}
													component={NavLink}
													to={path}
												>
													{label}
												</MenuItem>
											);
										})}
									</MenuList>
								</ClickAwayListener>
							</Paper>
						</Grow>
					)}
				</Popper>
			</Box>
		</>
	);
}
